import { Box, HStack, IconButton, NativeSelect, Switch } from "@chakra-ui/react"
import useCopyToClipboard, {
  UseCopyToClipboardReturn
} from "@pathwright/utils/src/clipboard/useCopyToClipboard"
import KeyControls from "@pathwright/utils/src/keyboard/KeyControls"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import Link, {
  LinkProps,
  useHandleTo
} from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Command } from "cmdk"
import { ComponentProps, ReactNode } from "react"
import { PWAdminCmdKOption, PWAdminCmdKOptionWithKeyControl } from "./types"

// Copy the "copy" string of an option to clipboard.
function copyOptionToClipboard(
  option: PWAdminCmdKOption,
  copyToClipboard: UseCopyToClipboardReturn
) {
  copyToClipboard(option.copy!, {
    title: `Copied ${option.label}`,
    description: ""
  })
}

export function KeyControlHandler({
  option
}: {
  option: PWAdminCmdKOptionWithKeyControl
}) {
  const copyToClipboard = useCopyToClipboard()
  const handler =
    option.onSelect || (() => copyOptionToClipboard(option, copyToClipboard))

  useKeyControl(option.keyControl, handler)
  return null
}

// Conditionally wraps with Link when option has link props.
function ConditionalOptionLink({
  option,
  children,
  ...linkProps
}: {
  option: PWAdminCmdKOption
  children: ReactNode
} & LinkProps) {
  if ("to" in option || "href" in option) {
    const {
      // @ts-expect-error: keyControl is not a valid prop for Link, but also may not exist in the option.
      keyControl,
      ...optionlinkProps
    } = option

    return (
      <Link
        w="100%"
        fontSize=".8rem"
        colorPalette="whiteAlpha"
        bg="transparent"
        _hover={{ bg: "transparent" }}
        textDecoration="none"
        minW="0"
        {...optionlinkProps}
        {...linkProps}
      >
        {children}
      </Link>
    )
  }

  return children
}

export type PWAdminCmdKItemProps = { option: PWAdminCmdKOption } & Omit<
  ComponentProps<typeof Command.Item>,
  "onSelect"
>

const PWAdminCmdKItem = ({
  option,
  ...commandItemProps
}: PWAdminCmdKItemProps) => {
  const handleTo = useHandleTo()
  const copyToClipboard = useCopyToClipboard()

  // Prefer the option's onSelect prop, falling back to copy, then navigation.
  function handleSelect(option: PWAdminCmdKOption) {
    if (option.disabled) return

    if (option.onSelect) {
      option.onSelect()
    } else if (option.copy) {
      copyOptionToClipboard(option, copyToClipboard)
    } else if ("href" in option && option.href) {
      window.open(option.href, "_blank")
    } else if ("to" in option && option.to) {
      handleTo(option.to)
    }
  }

  return (
    <HStack
      as={Command.Item}
      className="PWAdminCmdKItem"
      disabled={!!option.disabled}
      onSelect={() => handleSelect(option)}
      w="100%"
      minH="2.8em"
      px={2}
      py={1}
      fontSize=".8rem"
      color="whiteAlpha.500"
      fontWeight="semibold"
      borderRadius="lg"
      cursor={option.disabled ? "not-allowed" : "pointer"}
      opacity={option.disabled ? 0.6 : 1}
      title={option.disabledReason}
      css={{
        ["&[data-selected='true']"]: {
          bg: "whiteAlpha.300"
        }
      }}
      {...commandItemProps}
    >
      <ConditionalOptionLink
        option={option}
        onClick={(e) => e.stopPropagation()}
      >
        <HStack flexGrow={1} w="100%" justifyContent="flex-start">
          {option.icon && <Pathicon icon={option.icon} {...option.iconProps} />}
          <Box as="span" lineClamp={1}>
            {option.label}
          </Box>
        </HStack>
      </ConditionalOptionLink>
      {!!option.copy && (
        <IconButton
          children={<Pathicon icon="copy" />}
          colorPalette="whiteAlpha"
          variant="ghost"
          minH="2em"
          minW="2em"
          borderRadius="full"
          aria-label="Copy"
          onClick={(e) => {
            // We could forego the onClick handler entirely and let the Command.Item's
            // onSelect handle it, which we might do, but this might be clearer?
            e.stopPropagation() // prevents duplicate onSelect
            copyOptionToClipboard(option, copyToClipboard)
          }}
        />
      )}
      {"keyControl" in option && (
        <>
          <KeyControlHandler option={option} />
          <KeyControls keyControl={option.keyControl} color="whiteAlpha.600" />
        </>
      )}
      {"active" in option && (
        <DarkMode>
          <Box
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <Switch.Root checked={option.active}>
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label />
            </Switch.Root>
          </Box>
        </DarkMode>
      )}
      {"select" in option && !!option.select && (
        <DarkMode>
          <NativeSelect.Root
            size="sm"
            w="auto"
            minW="7.5em"
            disabled={option.disabled}
            css={{
              "& option": { bg: "gray.800", color: "white" }
            }}
          >
            <NativeSelect.Field
              value={option.select.value}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => option.select!.onChange(e.target.value)}
            >
              {option.select.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator>
              <Pathicon icon="chevron-down" />
            </NativeSelect.Indicator>
          </NativeSelect.Root>
        </DarkMode>
      )}
      {"link" in option && (
        <Link asChild {...option.link}>
          <IconButton
            colorPalette="whiteAlpha"
            variant="ghost"
            minH="2em"
            minW="2em"
            borderRadius="full"
            aria-label="Link"
            onClick={(e) => e.stopPropagation()}
            children={
              <Pathicon
                icon={option.link?.icon || "external-link"}
                {...option.link?.iconProps}
              />
            }
          />
        </Link>
      )}
    </HStack>
  )
}

export default PWAdminCmdKItem
